import React, { useState, useEffect, useRef, useMemo } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { Search, X, FileText, Users, Building2, CheckSquare, ShoppingCart, Clock } from "lucide-react";

const CATEGORIE = {
  progetti:   { icon:<FileText size={13}/>,    col:"#7c3aed", label:"Commessa" },
  clienti:    { icon:<Building2 size={13}/>,   col:"#2563eb", label:"Cliente" },
  dipendenti: { icon:<Users size={13}/>,        col:"#059669", label:"Dipendente" },
  tasks:      { icon:<CheckSquare size={13}/>,  col:"#d97706", label:"Attività" },
  rapportini: { icon:<FileText size={13}/>,     col:"#0891b2", label:"Rapportino" },
  nf:         { icon:<ShoppingCart size={13}/>, col:"#dc2626", label:"Richiesta" },
  scadenze:   { icon:<Clock size={13}/>,        col:"#6b7280", label:"Scadenza" },
};

import { useNav } from "../contexts/NavContext";

export default function GlobalSearch({ onNavigate, onClose }) {
  const { openEntity } = useNav();
  const { effectiveOrgId } = useOrg();
  const [testo, setTesto] = useState("");
  const [risultati, setRisultati] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selIdx, setSelIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(()=>{ inputRef.current?.focus(); },[]);

  useEffect(()=>{
    if(testo.trim().length<2){ setRisultati([]); return; }
    const t = setTimeout(()=>cerca(testo.trim()), 250);
    return ()=>clearTimeout(t);
  },[testo]);

  const cerca = async (q) => {
    if(!effectiveOrgId) return;
    setLoading(true);
    const ql = q.toLowerCase();
    const res = [];

    try {
      // Progetti/commesse
      const projSnap = await getDocs(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)));
      projSnap.docs.forEach(d=>{
        const p = d.data();
        if(p.isParent) return;
        if((p.name||"").toLowerCase().includes(ql)||(p.clienteNome||"").toLowerCase().includes(ql)){
          res.push({ id:d.id, tipo:"progetti", titolo:p.name, sub:p.clienteNome||"", statoCommessa:p.statoCommessa||"", codiceCommessa:p.codiceCommessa||"", navigateTo:{page:"project",projectId:d.id,project:{id:d.id,...p}} });
        }
      });
      // Clienti
      const clSnap = await getDocs(query(collection(db,"clienti"),where("organizationId","==",effectiveOrgId)));
      clSnap.docs.forEach(d=>{
        const c = d.data();
        const nome = c.ragioneSociale||c.nome||"";
        if(nome.toLowerCase().includes(ql)||(c.piva||"").includes(ql)){
          res.push({ id:d.id, tipo:"clienti", titolo:nome, sub:c.piva||c.codiceFiscale||"", navigateTo:{page:"clienti"}, entityData:{id:d.id,...c} });
        }
      });
      // Dipendenti
      const dipSnap = await getDocs(query(collection(db,"dipendenti"),where("organizationId","==",effectiveOrgId)));
      dipSnap.docs.forEach(d=>{
        const dip = d.data();
        const nome = `${dip.nome||""} ${dip.cognome||""}`.trim();
        if(nome.toLowerCase().includes(ql)||(dip.mansione||"").toLowerCase().includes(ql)){
          res.push({ id:d.id, tipo:"dipendenti", titolo:nome, sub:dip.mansione||"", navigateTo:{page:"dipendenti"}, entityData:{id:d.id,...dip} });
        }
      });
      // Tasks
      const taskSnap = await getDocs(query(collection(db,"tasks"),where("organizationId","==",effectiveOrgId)));
      taskSnap.docs.forEach(d=>{
        const t = d.data();
        if((t.title||"").toLowerCase().includes(ql)){
          res.push({ id:d.id, tipo:"tasks", titolo:t.title, sub:t.projectName||"", navigateTo:{page:"my-tasks"}, entityData:{id:d.id,...t} });
        }
      });
      // Rapportini
      const rapSnap = await getDocs(query(collection(db,"rapportini"),where("orgId","==",effectiveOrgId)));
      rapSnap.docs.forEach(d=>{
        const r = d.data();
        if((r.numero||"").toLowerCase().includes(ql)||(r.nomeCliente||"").toLowerCase().includes(ql)||(r.lavorazioni||"").toLowerCase().includes(ql)){
          res.push({ id:d.id, tipo:"rapportini", titolo:`Rapportino ${r.numero||""}`, sub:r.nomeCliente||"", navigateTo:{page:"registro-rapportini"} });
        }
      });
      // NF
      const nfSnap = await getDocs(query(collection(db,"nuoveForniture"),where("organizationId","==",effectiveOrgId)));
      nfSnap.docs.forEach(d=>{
        const n = d.data();
        if(!n.eliminata && (n.descrizione||"").toLowerCase().includes(ql)){
          res.push({ id:d.id, tipo:"nf", titolo:n.descrizione?.slice(0,60)||"NF", sub:n.commessaNome||"", navigateTo:{page:"centro-acquisti"} });
        }
      });
      // Scadenze
      const scadSnap = await getDocs(query(collection(db,"scadenzario"),where("organizationId","==",effectiveOrgId)));
      scadSnap.docs.forEach(d=>{
        const s = d.data();
        if((s.voce||"").toLowerCase().includes(ql)){
          res.push({ id:d.id, tipo:"scadenze", titolo:s.voce, sub:s.dataScadenza?new Date(s.dataScadenza+"T00:00:00").toLocaleDateString("it-IT"):"", navigateTo:{page:"scadenziario"} });
        }
      });
    } catch(e){ console.error("Search error:",e); }

    setRisultati(res.slice(0,20));
    setSelIdx(0);
    setLoading(false);
  };

  const apri = (r) => {
    // Per entità con scheda drawer, apri il drawer invece di navigare alla pagina
    if (r.tipo === "clienti" && r.entityData) {
      openEntity("cliente", r.entityData);
      onClose?.();
      return;
    }
    if (r.tipo === "dipendenti" && r.entityData) {
      openEntity("dipendente", r.entityData);
      onClose?.();
      return;
    }
    if (r.tipo === "tasks" && r.entityData) {
      openEntity("task", r.entityData);
      onClose?.();
      return;
    }
    if(onNavigate) onNavigate(r.navigateTo);
    onClose?.();
  };

  const handleKey = (e) => {
    if(e.key==="ArrowDown"){ e.preventDefault(); setSelIdx(i=>Math.min(i+1,risultati.length-1)); }
    if(e.key==="ArrowUp"){ e.preventDefault(); setSelIdx(i=>Math.max(i-1,0)); }
    if(e.key==="Enter" && risultati[selIdx]) apri(risultati[selIdx]);
    if(e.key==="Escape") onClose?.();
  };

  // Raggruppa per tipo
  const gruppi = useMemo(()=>{
    const g = {};
    risultati.forEach(r=>{ if(!g[r.tipo]) g[r.tipo]=[]; g[r.tipo].push(r); });
    return g;
  },[risultati]);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9000,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"80px 20px 20px"}}
      onClick={e=>e.target===e.currentTarget&&onClose?.()}>
      <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:600,boxShadow:"0 25px 80px rgba(0,0,0,.25)",overflow:"hidden"}}>
        {/* Input */}
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"14px 16px",borderBottom:"1px solid #f3f4f6"}}>
          <Search size={18} color="#9ca3af"/>
          <input ref={inputRef} value={testo} onChange={e=>setTesto(e.target.value)} onKeyDown={handleKey}
            placeholder="Cerca commesse, clienti, dipendenti, attività..."
            style={{flex:1,border:"none",outline:"none",fontSize:15,fontFamily:"'Inter',sans-serif",color:"#111827"}}/>
          {loading && <div style={{width:16,height:16,border:"2px solid #e5e7eb",borderTopColor:"#7c3aed",borderRadius:"50%",animation:"spin 1s linear infinite"}}/>}
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:4,display:"flex"}}>
            <X size={16}/>
          </button>
        </div>

        {/* Risultati */}
        <div style={{maxHeight:440,overflowY:"auto"}}>
          {testo.length<2 && (
            <div style={{padding:32,textAlign:"center",color:"#9ca3af",fontSize:13}}>
              Digita almeno 2 caratteri per cercare
            </div>
          )}
          {testo.length>=2 && risultati.length===0 && !loading && (
            <div style={{padding:32,textAlign:"center",color:"#9ca3af",fontSize:13}}>
              Nessun risultato per "<strong style={{color:"#374151"}}>{testo}</strong>"
            </div>
          )}
          {Object.entries(gruppi).map(([tipo, items])=>{
            const cat = CATEGORIE[tipo]||CATEGORIE.progetti;
            return (
              <div key={tipo}>
                <div style={{padding:"8px 16px 4px",fontSize:10,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:".06em",background:"#f9fafb"}}>
                  {cat.label}s
                </div>
                {items.map((r,i)=>{
                  const isSelected = risultati.indexOf(r)===selIdx;
                  return (
                    <button key={r.id} onClick={()=>apri(r)}
                      style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"10px 16px",border:"none",borderBottom:"1px solid #f9fafb",
                        background:isSelected?"#faf5ff":"#fff",cursor:"pointer",textAlign:"left",fontFamily:"'Inter',sans-serif"}}
                      onMouseEnter={()=>setSelIdx(risultati.indexOf(r))}>
                      <div style={{width:30,height:30,borderRadius:8,background:cat.col+"15",display:"flex",alignItems:"center",justifyContent:"center",color:cat.col,flexShrink:0}}>
                        {cat.icon}
                      </div>
                      <div style={{flex:1,overflow:"hidden"}}>
                        <div style={{fontSize:13,fontWeight:600,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {r.statoCommessa==="chiusa"&&<span style={{marginRight:5,fontSize:11}} title="Commessa chiusa">🔒</span>}
                          <span style={{opacity:r.statoCommessa==="chiusa"?0.6:1}}>{r.titolo}</span>
                          {r.statoCommessa==="chiusa"&&<span style={{marginLeft:6,fontSize:10,padding:"1px 6px",borderRadius:10,background:"#fee2e2",color:"#dc2626",fontWeight:700}}>Chiusa</span>}
                        </div>
                        {r.sub&&<div style={{fontSize:11,color:"#9ca3af",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.sub}</div>}
                      </div>
                      <span style={{fontSize:10,color:"#d1d5db",flexShrink:0}}>→</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer hint */}
        <div style={{padding:"8px 16px",borderTop:"1px solid #f3f4f6",display:"flex",gap:12,fontSize:10,color:"#9ca3af"}}>
          <span>↑↓ naviga</span>
          <span>↵ apri</span>
          <span>Esc chiudi</span>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
